<template>
    <section class="rightColumn search__gridRightColumn hideOnMobile--block">
        <div
            v-if="response"
            id="sidebar__main"
            tabindex="0"
            role="dialog"
            aria-hidden="true"
            class="searchSidebar">
            <SidebarFilter
                :key="sidebarKey"
                @selected-accuracy="emittedAccuracyValue"
                @selected-contentTypes="emittedContentTypeValue"
                @selected-region="emittedRegionValue"
                @selected-countries="emittedCountriesValue"
                @selected-regionalBoost="emittedRegionalBoostValue"
                @selected-interval="emittedIntervalValue"
                @selected-archive="emittedArchiveValue"
                @selected-sort="emittedSortValue"
                @selected-category="emittedCategoryValue"
                @selected-sub-category="emittedSubCategoryValue"
                @selected-language="emittedLanguageValue"
                @excluded-regions="emittedExcludedRegions" />
            <!-- reset tag area -->
            <div
                v-if="changed || checkIfFilterApplied()"
                class="facetedSearch__reset flex flex__justifyContent--center flex__alignItems--center">
                <button
                    type="button"
                    class="facetedSearch__resetText"
                    data-gtm="facetedSearch__resetAllTags"
                    @click="resetFilter">
                    {{ trans('RESET_ALL_FILTERS') }}
                    <svg
                        class="searchIcon--16 searchIcon__left facetedSearch__resetIcon"
                        height="16"
                        width="16"
                        aria-hidden="true">
                        <use href="#iconArrowClockwiseBold" />
                    </svg>
                </button>
            </div>
        </div>
        <FacetedSearch />
        <RegionalBooster />
        <div
            v-if="topic.idcontent"
            class="topfact__card searchCard">
            <div class="topfact__top">
                {{ trans('MOST_IMPORTANT_FACTS') }}
            </div>
            <div class="topfact__headline">
                {{ topic.keyword }}
            </div>
            <div class="topfact__content">
                {{ topic.heading }}
            </div>
            <div class="topfact__button">
                <a
                    :href="path(topic)"
                    class="button button--primary"
                    data-gtm="search__topic--overview"
                    @keydown.left="changeFocus">
                    {{ trans('GET_ON_OVERVIEW') }}
                </a>
            </div>
        </div>
        <div
            v-if="topics.length > 0"
            class="topic__title">
            {{ trans('OTHER_INTERESTING_TOPICS') }}
        </div>
        <div class="topic__container">
            <a
                v-for="(item) in topics"
                :key="item.idcontent"
                class="topic__card"
                :data-gtm="`search__topic--list${ item.idcontent }`"
                :href="path(item)"
                @keydown.left="changeFocus">
                <div class="topic__rect"></div>
                <div class="topic__content">{{ item.heading }}</div>
            </a>
        </div>
    </section>
</template>
<script>
    import FacetedSearch from './components/facetedSearch';
    import RegionalBooster from './components/regionalBoosterBox';
    import SidebarFilter from './sidebarFilter';
    import Translator from 'tools/trans';
    import { getDiff, isEqual } from './getDiff';
    import { pushToDataLayer } from 'search/dataLayer';
    import { mapGetters, mapActions } from 'vuex';
    import { defaultLanguageValue } from './store/search.module';

    const ZERO_VALUE = '0';
    const AND_VALUE = 'and';
    const DATALAYER_DELAY_MS = 5000;
    const FILTER_DELAY_MS = 2000;
    const FILTER_INDUSTRY_LOCATION_DELAY_MS = 4000;
    const FILTERS_NEED_MORE_DELAY = ['category', 'subCategory', 'isoregion', 'countries'];
    const COUNTRIES = 'countries';
    export default {
        name: 'RightColumn',
        components: {
            FacetedSearch,
            RegionalBooster,
            SidebarFilter,
        },
        data() {
            return {
                contentFilters: this.getTabGroup,
                accuracyValue: this.accuracy,
                contentTypeValue: this.getTabGroup,
                intervalValue: this.interval,
                countriesValue: this.location,
                idRelevanceValue: this.idRelevance,
                categoryValue: this.category,
                subCategoryValue: this.subCategory,
                regionValue: this.isoregion,
                isRegionPrefValue: this.focus,
                sortValue: this.sort,
                languageValue: this.language,
                excludedRegions: [],
                changed: false,
                initialObj: {
                    sort: this.sort,
                    contentTypes: this.getTabGroup,
                    isoregion: this.isoregion,
                    countries: !Array.isArray(this.location) ?
                        this.location?.split(',').map(function(x) {
                            return parseInt(x);
                        })
                        :
                        this.location
                    ,
                    isRegionPref: this.focus,
                    accuracy: this.accuracy,
                    interval: this.interval,
                    idRelevance: this.idRelevance,
                    category: this.category,
                    subCategory: this.subCategory,
                    language: this.language,
                },
                debounceTimer: null,
                sidebarKey: 0,
            };
        },
        computed: {
            temporaryObj() {
                return {
                    sort: this.sortValue,
                    contentTypes: this.contentFilters,
                    isoregion: this.regionValue,
                    countries: Array.isArray(this.countriesValue) ? this.countriesValue : (this.countriesValue?.split(',') ?? []).map(x => parseInt(x)),
                    accuracy: this.accuracyValue,
                    interval: this.intervalValue,
                    idRelevance: this.idRelevanceValue,
                    category: this.categoryValue,
                    subCategory: this.subCategoryValue,
                    isRegionPref: this.isRegionPrefValue,
                    language: this.languageValue,
                };
            },
            ...mapGetters('search', [
                'getTabGroup',
                'activeTab',
                'topic',
                'topics',
                'countries',
                'sort',
                'focus',
                'location',
                'intervalData',
                'interval',
                'accuracy',
                'idRelevance',
                'category',
                'subCategory',
                'isoregion',
                'language',
                'response',
                'path',
            ]),
        },
        watch: {
            temporaryObj: {
                handler(value) {
                    this.changed = this.checkIfChanged(this.initialObj, value);
                    this.handleFilterChange();
                },
                deep: true,
            },
        },
        methods: {
            trans: Translator.trans,
            ...mapActions('search', ['resetFilters', 'resetPageNumber', 'setFilters', 'setActiveTab', 'calculateWhichBannerToShow']),
            handleFilterChange() {
                // Trigger the debounced search request
                if (this.changed) {
                    this.debouncedSearchRequest();
                }
            },
            debouncedSearchRequest() {
                // Clear the existing timeout to restart the debounce timer
                clearTimeout(this.debounceTimer);

                // check that which filters have changed
                const diffObj = getDiff(this.initialObj, this.temporaryObj);
                if (this.excludedRegions.includes(parseInt(this.temporaryObj.isoregion))) {
                    if (diffObj[COUNTRIES]) {
                        diffObj[COUNTRIES] = [];
                    }
                }
                // need only latest key in object considered
                const latestKey = Object.keys(diffObj)[Object.keys(diffObj).length - 1];
                const delayDuration = FILTERS_NEED_MORE_DELAY.includes(latestKey) ? FILTER_INDUSTRY_LOCATION_DELAY_MS : FILTER_DELAY_MS;
                // Set a new timeout for 4 seconds when filter changes is included industry or location and 2 sec for others
                this.debounceTimer = setTimeout(() => {
                    // This function will be called after a 4-second or 2-sec delay of no further changes
                    if (this.changed) {
                        this.applyFilter();
                    }
                }, delayDuration);

            },
            updateContentTypes(value) {
                this.contentFilters = value;
            },
            emittedAccuracyValue(filterValue) {
                this.accuracyValue = filterValue;
            },
            emittedContentTypeValue(filterValue) {
                this.contentFilters = filterValue;
            },
            emittedCountriesValue(filterValue) {
                this.countriesValue = filterValue;
            },
            emittedIntervalValue(filterValue) {
                this.intervalValue = filterValue;
            },
            emittedArchiveValue(filterValue) {
                this.idRelevanceValue = filterValue;
            },
            emittedCategoryValue(filterValue) {
                this.categoryValue = filterValue;
            },
            emittedSubCategoryValue(filterValue) {
                this.subCategoryValue = filterValue;
            },
            emittedRegionValue(filterValue) {
                this.regionValue = filterValue;
            },
            emittedSortValue(filterValue) {
                this.sortValue = filterValue;
            },
            emittedRegionalBoostValue(filterValue) {
                this.isRegionPrefValue = filterValue;
            },
            emittedLanguageValue(filterValue) {
                this.languageValue = filterValue;
            },
            emittedExcludedRegions(data) {
                this.excludedRegions = data;
            },
            async resetFilter() {
                await this.resetPageNumber();
                await this.setActiveTab(null);
                await this.resetFilters();
                this.changed = false;
                this.sidebarKey += 1;
            },
            checkIfChanged(obj1, obj2) {
                for (const key in obj1) {
                    if (Array.isArray(obj1[key])) {
                        if (isEqual(obj1[key], obj2[key])) {
                            continue;
                        } else {
                            return true;
                        }
                    } if (obj1[key] === obj2[key]) {
                        continue;
                    } else {
                        return true;
                    }
                }

                return false;
            },
            async applyFilter() {
                const diffObj = getDiff(this.initialObj, this.temporaryObj);
                if (this.excludedRegions.includes(parseInt(this.temporaryObj.isoregion))) {
                    if (diffObj['countries']) {
                        diffObj['countries'] = [];
                    }
                }
                await this.resetPageNumber();
                await this.setFilters(diffObj);
                this.initialObj = {
                    sort: this.sort,
                    contentTypes: this.getTabGroup,
                    isoregion: this.isoregion,
                    countries: !Array.isArray(this.location) ?
                        this.location?.split(',').map(function(x) {
                            return parseInt(x);
                        })
                        :
                        this.location
                    ,
                    isRegionPref: this.focus,
                    accuracy: this.accuracy,
                    interval: this.interval,
                    idRelevance: this.idRelevance,
                    category: this.category,
                    subCategory: this.subCategory,
                    language: this.language,
                };
                this.changed = false;
                await this.calculateWhichBannerToShow();
                // push to data layer
                setTimeout(() => {
                    pushToDataLayer();
                }, DATALAYER_DELAY_MS);
            },
            checkIfFilterApplied() {
                const queryString = window.location.search;
                const uparams = new URLSearchParams(queryString);
                if (!uparams) {
                    return false;
                }
                if (uparams.get('interval') && uparams.get('interval') !== ZERO_VALUE) {
                    return true;
                }
                if (uparams.get('tabGroup')?.length > 0) {
                    return true;
                }
                if (uparams.get('accuracy') && uparams.get('accuracy') !== AND_VALUE) {
                    return true;
                }
                if (uparams.get('isoregion')) {
                    if (uparams.get('isoregion') !== ZERO_VALUE) {
                        return true;
                    } else if (uparams.get('region[]') && uparams.get('region[]') !== '') {
                        return true;
                    }
                } else if (uparams.get('region[]') && uparams.get('region[]') !== '') {
                    return true;
                }
                if (uparams.get('idRelevance') && uparams.get('idRelevance') !== ZERO_VALUE) {
                    return true;
                }
                if (uparams.get('category[]') && uparams.get('category[]') !== ZERO_VALUE) {
                    return true;
                }
                if (uparams.get('language') && uparams.get('language') !== defaultLanguageValue[window.stGlobals.locale]) {
                    return true;
                }

                return false;
            },
        },
        mounted() {
            this.contentFilters = this.getTabGroup;
            this.accuracyValue = this.accuracy;
            this.contentTypeValue = this.getTabGroup;
            this.intervalValue = this.interval;
            this.countriesValue = this.location;
            this.idRelevanceValue = this.idRelevance;
            this.categoryValue = this.category;
            this.subCategoryValue = this.subCategory;
            this.regionValue = this.isoregion;
            this.isRegionPrefValue = this.focus;
            this.sortValue = this.sort;
            this.languageValue = this.language;
            this.initialObj = {
                sort: this.sort,
                contentTypes: this.getTabGroup,
                isoregion: this.isoregion,
                countries: !Array.isArray(this.location) ?
                    this.location?.split(',').map(function(x) {
                        return parseInt(x);
                    })
                    :
                    this.location
                ,
                isRegionPref: this.focus,
                accuracy: this.accuracy,
                interval: this.interval,
                idRelevance: this.idRelevance,
                category: this.category,
                subCategory: this.subCategory,
                language: this.language,
            };
        },
    };
</script>
